// src/components/Meta.jsx
// React 19+ (no Helmet). Drop in and import on each page.

import React from "react";

/* ----------------------------- helpers ----------------------------- */
function robotsContent({
  index = true,
  follow = true,
  noarchive = false,
  nosnippet = false,
  noimageindex = false,
  nocache = false,
  maxImagePreview = "large", // "none" | "standard" | "large"
  maxSnippet, // number
  maxVideoPreview, // number
} = {}) {
  const p = [];
  p.push(index ? "index" : "noindex");
  p.push(follow ? "follow" : "nofollow");
  if (noarchive) p.push("noarchive");
  if (nosnippet) p.push("nosnippet");
  if (noimageindex) p.push("noimageindex");
  if (nocache) p.push("nocache");
  if (maxImagePreview) p.push(`max-image-preview:${maxImagePreview}`);
  if (typeof maxSnippet === "number") p.push(`max-snippet:${maxSnippet}`);
  if (typeof maxVideoPreview === "number") p.push(`max-video-preview:${maxVideoPreview}`);
  return p.join(",");
}

function abs(base, path = "") {
  if (!path) return base.replace(/\/+$/, "/");
  if (/^https?:\/\//i.test(path)) return path;
  return `${base.replace(/\/+$/, "")}/${path.replace(/^\/+/, "")}`;
}

function uniqBy(arr, key) {
  const seen = new Set();
  return (arr || []).filter((x) => {
    const k = key(x);
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

function JsonLd({ data }) {
  if (!data) return null;
  const blocks = Array.isArray(data) ? data : [data];
  return blocks.map((node, i) => (
    <script
      key={i}
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(node) }}
    />
  ));
}

/* ------------------------- NEW: keywords helpers ------------------------- */
const STOPWORDS_FR = new Set([
  "de","du","des","la","le","les","et","à","en","au","aux","pour","sur","dans","par","avec",
  "un","une","vos","notre","votre","nos","ses","son","sa","ce","ces","cette","plus","se",
  "d’","l’","chez","ou","où"
]);
const STOPWORDS_EN = new Set([
  "the","a","an","and","or","for","to","in","of","on","with","by","at","from","your","our",
  "their","this","that","these","those","is","are","be","as","we","you"
]);

function tokenize(text = "", lang = "fr") {
  const stop = lang === "fr" ? STOPWORDS_FR : STOPWORDS_EN;
  return (text || "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[^\p{L}\p{N}\s-]/gu, "")
    .split(/\s+/)
    .filter((w) => w && !stop.has(w) && w.length > 1);
}

function toKeywordsContent(arr, maxChars = 900) {
  const out = [];
  let total = 0;
  for (const k of arr) {
    const piece = out.length ? `, ${k}` : k;
    if (total + piece.length > maxChars) break;
    out.push(k);
    total += piece.length;
  }
  return out.join(", ");
}

function buildAutoKeywords({ lang, title, description, services, localBusiness }) {
  const list = [];

  // Language seed terms
  const coreFR = [
    "service de nettoyage","nettoyage","ménage","nettoyage bureaux","ménage à domicile",
    "lavage de vitres","nettoyage fin de chantier","société de nettoyage","entreprise de nettoyage"
  ];
  const coreEN = [
    "cleaning service","cleaning","office cleaning","house cleaning",
    "window cleaning","post-construction cleaning","cleaning company"
  ];
  // Small set of additional common search keywords to improve auto keywords
  const COMMON_KEYWORDS_FR = [
    "nettoyage professionnel","entretien","ménage professionnel","appel d'offres nettoyage",
    "prix nettoyage","devis gratuit","nettoyage écologique"
  ];
  const COMMON_KEYWORDS_EN = [
    "professional cleaning","maintenance","housekeeping","cleaning quote",
    "cleaning price","free quote","eco cleaning"
  ];

  list.push(...(lang === "fr" ? [...COMMON_KEYWORDS_FR, ...coreFR] : [...COMMON_KEYWORDS_EN, ...coreEN]));

  // From title & description
  list.push(...tokenize(title, lang));
  list.push(...tokenize(description, lang));

  // From services
  if (Array.isArray(services)) {
    services.forEach((s) => {
      if (s?.name) list.push(s.name);
      if (s?.serviceType) list.push(String(s.serviceType));
    });
  }

  // From locality
  const locals = new Set();
  if (localBusiness?.addressLocality) locals.add(localBusiness.addressLocality);
  if (localBusiness?.addressRegion) locals.add(localBusiness.addressRegion);
  if (Array.isArray(localBusiness?.areaServed)) {
    localBusiness.areaServed.forEach((a) => a && locals.add(a));
  }
  locals.forEach((loc) => {
    list.push(loc);
    if (lang === "fr") {
      list.push(`devis nettoyage ${loc}`);
      list.push(`tarif nettoyage ${loc}`);
    } else {
      list.push(`cleaning quote ${loc}`);
      list.push(`cleaning price ${loc}`);
    }
  });

  // dedupe and cap number of items
  return Array.from(new Set(list.map((x) => String(x).trim()).filter(Boolean))).slice(0, 60);
}

/* ------------------------------ component ------------------------------ */
/**
 * Meta
 * Key props for your setup:
 * - primaryDomain: canonical base (e.g., https://pjgservice.fr)
 * - altDomain: alternate domain for the other language (e.g., https://pjgservice.com)
 * - lang: 'fr' | 'en' (controls og:locale + hreflang auto-build)
 * - region: 'FR' by default
 * - path: page path (e.g., '/services')
 *
 * Optional:
 * - sameAs: social/profile URLs (array)
 * - services: [{ name, areaServed, serviceType }, ...] -> adds Service JSON-LD
 * - breadcrumbs: [{ name, url }, ...]
 * - faq: [{ q, a }, ...]
 * - pagination: { prevPath, nextPath } -> rel="prev/next"
 * - twitterSite: '@handle'
 * - ogImageWidth / ogImageHeight (numbers)
 * - googleSiteVerification: '...'  // emits <meta name="google-site-verification" ...>
 */
export default function Meta({
  /* Page basics */
  title = "PJG Service",
  description = "Entreprise de nettoyage pour particuliers et professionnels : ménage à domicile, bureaux, vitres, fin de chantier. Devis rapide et intervention fiable.",
  path = "/",
  siteName = "PJG Service",

  /* NEW: keywords */
  keywords,            // string | string[] (optional)
  autoKeywords = true, // build extra keywords automatically (default true)

  /* Domains & locale */
  primaryDomain = "https://pjgservice.fr",
  altDomain,                 // e.g., "https://pjgservice.com"
  lang = "fr",               // 'fr' (default) or 'en'
  region = "FR",             // 'FR' by default
  locale,                    // override if you want; else derived from lang/region
  ogType = "website",

  /* Social images */
  ogImage = "https://pjgservice.fr/pjg-og.png",
  ogImageWidth,
  ogImageHeight,
  twitterCard = "summary_large_image",
  twitterSite,               // '@pjgservice' (optional)

  /* Indexing */
  robots = {},
  googlebot = {},

  /* Hreflang extras (advanced). Usually you don't need to pass this. */
  alternates,                // custom alternates array; if omitted we auto-build

  /* JSON-LD toggles & content */
  includeWebSite = true,
  includeWebPage = true,
  localBusiness = {
    name: "PJG Service",
    telephone: "+33 6 12 34 56 78",
    priceRange: "€€",
    streetAddress: "12 Rue Exemple",
    postalCode: "75010",
    addressLocality: "Paris",
    addressRegion: "Île-de-France",
    addressCountry: "FR",
    areaServed: ["Paris", "Île-de-France"],
    opens: "08:30",
    closes: "18:00",
    opensSat: "09:00",
    closesSat: "14:00",
    logo: "https://pjgservice.fr/pjg-logo-512.png",
    image: "https://pjgservice.fr/pjg-og.png",
    sameAs: [],             // e.g., GBP/Facebook/LinkedIn URLs
    mapUrl: undefined,      // e.g., Google Maps Place URL
    geo: undefined,         // { latitude: 48.87, longitude: 2.35 }
  },
  services,                  // [{ name, areaServed, serviceType }]
  breadcrumbs,               // [{ name, url }]
  faq,                       // [{ q, a }]
  pagination,                // { prevPath, nextPath }

  /* Verification */
  googleSiteVerification,
}) {
  const canon = abs(primaryDomain, path);
  const currentLocale = locale || (lang === "fr" ? `${lang}_${region}` : lang);
  const otherLang = lang === "fr" ? "en" : "fr";
  const otherHrefLang = otherLang === "fr" ? "fr-FR" : "en";
  const currentHrefLang = lang === "fr" ? "fr-FR" : "en";

  // Build alternates automatically if none provided
  const computedAlternates = alternates && alternates.length
    ? alternates
    : [
        { hrefLang: currentHrefLang, href: abs(primaryDomain, path) },
        ...(altDomain
          ? [{ hrefLang: otherHrefLang, href: abs(altDomain, path) }]
          : []),
        { hrefLang: "x-default", href: abs(primaryDomain, path) },
      ];

  const uniqueAlternates = uniqBy(computedAlternates, (x) => `${x.hrefLang}|${x.href}`);

  const robotsStr = robotsContent(robots);
  const googlebotStr = robotsContent(googlebot);

  /* ---------------------------- JSON-LD blocks ---------------------------- */
  const websiteId = `${primaryDomain.replace(/\/+$/, "")}#website`;
  const orgId = `${primaryDomain.replace(/\/+$/, "")}#org`;
  const webPageId = `${canon}#webpage`;

  const websiteLd = includeWebSite
    ? {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "@id": websiteId,
        "name": siteName,
        "url": primaryDomain,
        "inLanguage": currentLocale.replace("_", "-"),
        "publisher": { "@id": orgId },
        "potentialAction": {
          "@type": "SearchAction",
          "target": `${primaryDomain}/search?q={query}`,
          "query-input": "required name=query"
        }
      }
    : null;

  const webPageLd = includeWebPage
    ? {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "@id": webPageId,
        "url": canon,
        "name": title,
        "isPartOf": { "@id": websiteId },
        "inLanguage": currentLocale.replace("_", "-"),
        "about": { "@id": orgId },
        "description": description
      }
    : null;

  const cleaningLd = localBusiness
    ? {
        "@context": "https://schema.org",
        "@type": "CleaningService",
        "@id": orgId,
        "name": localBusiness.name || siteName,
        "url": primaryDomain,
        "logo": localBusiness.logo,
        "image": localBusiness.image,
        "telephone": localBusiness.telephone,
        "priceRange": localBusiness.priceRange,
        "address": {
          "@type": "PostalAddress",
          "streetAddress": localBusiness.streetAddress,
          "postalCode": localBusiness.postalCode,
          "addressLocality": localBusiness.addressLocality,
          "addressRegion": localBusiness.addressRegion,
          "addressCountry": localBusiness.addressCountry
        },
        ...(localBusiness.geo
          ? { "geo": { "@type": "GeoCoordinates", ...localBusiness.geo } }
          : {}),
        ...(localBusiness.mapUrl ? { "hasMap": localBusiness.mapUrl } : {}),
        ...(Array.isArray(localBusiness.areaServed) && localBusiness.areaServed.length
          ? { "areaServed": localBusiness.areaServed }
          : {}),
        ...(Array.isArray(localBusiness.sameAs) && localBusiness.sameAs.length
          ? { "sameAs": localBusiness.sameAs }
          : {}),
        "openingHoursSpecification": [
          {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday"],
            "opens": localBusiness.opens,
            "closes": localBusiness.closes
          },
          {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Saturday"],
            "opens": localBusiness.opensSat,
            "closes": localBusiness.closesSat
          }
        ]
      }
    : null;

  const servicesLd =
    Array.isArray(services) && services.length
      ? services.map((s) => ({
          "@context": "https://schema.org",
          "@type": "Service",
          "name": s.name,
          "provider": { "@id": orgId },
          ...(s.areaServed ? { "areaServed": s.areaServed } : {}),
          ...(s.serviceType ? { "serviceType": s.serviceType } : {}),
        }))
      : null;

  const breadcrumbLd =
    Array.isArray(breadcrumbs) && breadcrumbs.length
      ? {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": breadcrumbs.map((b, i) => ({
            "@type": "ListItem",
            "position": i + 1,
            "name": b.name,
            "item": abs(primaryDomain, b.url || "/")
          }))
        }
      : null;

  const faqLd =
    Array.isArray(faq) && faq.length
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faq.map(({ q, a }) => ({
            "@type": "Question",
            "name": q,
            "acceptedAnswer": { "@type": "Answer", "text": a }
          }))
        }
      : null;

  const jsonLd = [
    websiteLd,
    webPageLd,
    cleaningLd,
    ...(servicesLd || []),
    breadcrumbLd,
    faqLd,
  ].filter(Boolean);

  /* --------------------- NEW: build <meta keywords> ---------------------- */
  let keywordsContent = "";
  // 1) if user passed string/array, honor it
  if (Array.isArray(keywords)) {
    const dedup = Array.from(new Set(keywords.map((k) => String(k).trim()).filter(Boolean)));
    keywordsContent = toKeywordsContent(dedup, 900);
  } else if (typeof keywords === "string" && keywords.trim()) {
    keywordsContent = keywords.trim();
  }
  // 2) auto-enrich, if enabled
  if (autoKeywords) {
    const autoList = buildAutoKeywords({ lang, title, description, services, localBusiness });
    if (keywordsContent) {
      const merged = Array.from(
        new Set(
          keywordsContent
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
            .concat(autoList)
        )
      );
      keywordsContent = toKeywordsContent(merged, 900);
    } else {
      keywordsContent = toKeywordsContent(autoList, 900);
    }
  }

  /* ------------------------------- render ------------------------------- */
  return (
    <>
      {/* Core SEO */}
      <title>{title}</title>
      <meta name="description" content={description} />
      {/* NEW: optional meta keywords */}
      {keywordsContent && <meta name="keywords" content={keywordsContent} />}
      <link rel="canonical" href={canon} />

      {/* Hreflang (auto + custom) */}
      {uniqueAlternates.map((alt) => (
        <link key={`${alt.hrefLang}-${alt.href}`} rel="alternate" hrefLang={alt.hrefLang} href={alt.href} />
      ))}

      {/* Pagination (optional) */}
      {pagination?.prevPath && <link rel="prev" href={abs(primaryDomain, pagination.prevPath)} />}
      {pagination?.nextPath && <link rel="next" href={abs(primaryDomain, pagination.nextPath)} />}

      {/* Robots */}
      <meta name="robots" content={robotsStr} />
      <meta name="googlebot" content={googlebotStr} />
      {googleSiteVerification && (
        <meta name="google-site-verification" content={googleSiteVerification} />
      )}

      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:locale" content={currentLocale.replace("-", "_")} />
      {/* Advertise the other locale too */}
      <meta property="og:locale:alternate" content={otherHrefLang.replace("-", "_")} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canon} />
      <meta property="og:image" content={ogImage} />
      {typeof ogImageWidth === "number" && <meta property="og:image:width" content={String(ogImageWidth)} />}
      {typeof ogImageHeight === "number" && <meta property="og:image:height" content={String(ogImageHeight)} />}
      <meta property="og:image:alt" content={`${siteName} — ${description}`} />

      {/* Twitter */}
      <meta name="twitter:card" content={twitterCard} />
      {twitterSite && <meta name="twitter:site" content={twitterSite} />}
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* Structured Data */}
      <JsonLd data={jsonLd} />
    </>
  );
}
